import { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useZoneEditorStore } from "../../state/zone-editor-store.js";
import { renderZoneImageLayer, renderZoneOverlayLayer } from "./zone-renderer.js";
import {
  canvasToPage,
  hitTestZone,
  hitTestHandle,
  resizeZone,
  computeFitToPage,
  type HandleId,
} from "./zone-math.js";

/**
 * R-125 (5.2 / 5.3) — Public handle exposed to the parent so the viewport
 * controls toolbar (which renders OUTSIDE the canvas) can fetch the live
 * canvas/page dimensions for fit-to-page / fit-to-width math.
 */
export interface ZoneEditorCanvasHandle {
  getCanvasSize: () => { width: number; height: number } | null;
  getPageSize: () => { width: number; height: number } | null;
}

interface ZoneEditorCanvasProps {
  /** Called when the user finishes drawing a new zone; parent must prompt for fieldId */
  onZoneDrawn?: (x: number, y: number, width: number, height: number) => void;
}

/**
 * R-157 — Zone editor renders TWO stacked canvases so the heavyweight page
 * bitmap is decoded once and only re-painted when the viewport / page / image
 * actually changes. The overlay canvas (drawing rect, zones, handles) repaints
 * per-frame but stays cheap because it has no bitmap blit. The image canvas is
 * `pointer-events: none`; the overlay handles all mouse input.
 *
 * Before the split, both layers shared a single canvas and the rAF effect
 * depended on `drawingRect` — every mousemove tore down + re-armed the loop,
 * which called `resize()` (canvas.width = ...), wiping the bitmap to
 * transparent for one frame. That was the user-visible flicker.
 */
export const ZoneEditorCanvas = forwardRef<ZoneEditorCanvasHandle, ZoneEditorCanvasProps>(
  function ZoneEditorCanvas({ onZoneDrawn }, ref) {
  // Image layer (back) — the expensive bitmap blit; rarely repainted.
  const imageCanvasRef = useRef<HTMLCanvasElement>(null);
  // Overlay layer (front) — zones, drawing rect, handles; repaints per frame
  // during active interaction. Also receives all pointer events.
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  // Wrapper that sizes both canvases — the ResizeObserver target.
  const wrapperRef = useRef<HTMLDivElement>(null);

  const bitmapRef = useRef<ImageBitmap | null>(null);
  const animFrameRef = useRef<number>(0);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const resizeHandleRef = useRef<HandleId | null>(null);
  const resizingFieldIdRef = useRef<string | null>(null);
  // track previous space-key tool mode so we can restore it on keyup
  const prePanModeRef = useRef<"select" | "draw">("select");
  // R-125 (5.3) — track when the user is panning via click-drag in select
  // mode (right-click, middle-click, or drag from empty page area). We don't
  // toggle toolMode so handles / zone hit-testing still works on the next click.
  const isPanDragRef = useRef(false);
  // R-125 (5.1) — guards the one-shot fit-to-page on first bitmap load per
  // (template, page). Keyed by imageUrl so navigating between pages re-fits.
  const fitDoneForUrlRef = useRef<string | null>(null);
  // R-157 — tracks live CSS canvas size, kept in sync via ResizeObserver.
  // Lives in a ref so it doesn't trigger React re-renders or invalidate the
  // overlay rAF loop on every resize tick.
  const canvasSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  const {
    pages,
    currentPageNumber,
    viewport,
    toolMode,
    zoneMappings,
    selectedZoneFieldId,
    ocrSuggestions,
    showDismissed,
    drawingRect,
    setViewport,
    setToolMode,
    selectZone,
    startDraw,
    updateDraw,
    cancelDraw,
    updateZone,
    removeZone,
  } = useZoneEditorStore();

  const currentPage = pages.find((p) => p.pageNumber === currentPageNumber);

  // ── Effect 1: load page image + trigger one-shot fit-to-page ─────────────
  useEffect(() => {
    let cancelled = false;
    if (!currentPage?.imageUrl) {
      bitmapRef.current = null;
      return;
    }

    const url = currentPage.imageUrl;
    (async () => {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        const bitmap = await createImageBitmap(blob);
        if (!cancelled) {
          bitmapRef.current = bitmap;
          // R-125 (5.1) — Once the bitmap is decoded, fit-to-page so the
          // operator sees the whole page on first load (was: started at scale
          // 1 with no offset, which on a 2000×3000 scan showed only the
          // top-left fragment behind a giant dark fill — the headline bug).
          // F-014 — only auto-fit when the operator hasn't already set a
          // viewport. On a remount after a draw/autosave the bitmap reloads,
          // but the store viewport is preserved (loadTemplate keeps it for the
          // same template); refitting here would override the operator's zoom.
          const liveViewport = useZoneEditorStore.getState().viewport;
          const viewportIsPristine =
            liveViewport.scale === 1 && liveViewport.x === 0 && liveViewport.y === 0;
          if (fitDoneForUrlRef.current !== url && viewportIsPristine) {
            const canvas = imageCanvasRef.current;
            if (canvas) {
              const rect = canvas.getBoundingClientRect();
              const cw = rect.width;
              const ch = rect.height;
              const pw = currentPage.imageWidth || bitmap.width;
              const ph = currentPage.imageHeight || bitmap.height;
              if (cw > 0 && ch > 0 && pw > 0 && ph > 0) {
                setViewport(computeFitToPage(cw, ch, pw, ph));
                fitDoneForUrlRef.current = url;
              }
            }
          } else if (fitDoneForUrlRef.current !== url) {
            // Mark the url as "handled" so we don't keep retrying to fit a
            // page the operator has already zoomed.
            fitDoneForUrlRef.current = url;
          }
          // Image bitmap just changed — paint the image layer immediately so
          // the operator sees it without waiting on the next viewport tick.
          paintImageLayer();
        }
      } catch {
        // If fetch fails just leave bitmap null; canvas renders without it
        if (!cancelled) {
          bitmapRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage?.imageUrl, currentPage?.imageWidth, currentPage?.imageHeight]);

  // ── R-157 — ResizeObserver lives in its OWN effect so canvas dimensions are
  // never reset by the overlay/image render effects (which previously caused
  // the bitmap to flicker to blank every time `drawingRect` changed).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const dpr = window.devicePixelRatio ?? 1;

    function applySize() {
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      canvasSizeRef.current = { width: rect.width, height: rect.height };
      for (const canvas of [imageCanvasRef.current, overlayCanvasRef.current]) {
        if (!canvas) continue;
        const targetW = Math.max(1, Math.round(rect.width * dpr));
        const targetH = Math.max(1, Math.round(rect.height * dpr));
        // Only assign when the backing-store size actually changes — assigning
        // canvas.width clears the bitmap, which is what previously caused the
        // flicker. Comparing first means a no-op resize tick stays a no-op.
        if (canvas.width !== targetW) canvas.width = targetW;
        if (canvas.height !== targetH) canvas.height = targetH;
      }
      // After any real size change, repaint the image layer so the back
      // canvas isn't blank between the resize and the next viewport edit.
      paintImageLayer();
    }

    const ro = new ResizeObserver(applySize);
    ro.observe(wrapper);
    applySize();
    return () => {
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── R-157 — Image layer painter. Pure function over (bitmap, viewport,
  // page dimensions, canvas size). Called only when those inputs change, NOT
  // on every drawingRect mousemove.
  const paintImageLayer = useCallback(() => {
    const canvas = imageCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio ?? 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cssW = canvas.width / dpr;
    const cssH = canvas.height / dpr;
    const pageW = currentPage?.imageWidth ?? cssW;
    const pageH = currentPage?.imageHeight ?? cssH;

    renderZoneImageLayer({
      ctx,
      viewport,
      pageBitmap: bitmapRef.current,
      pageWidth: pageW,
      pageHeight: pageH,
      canvasWidth: cssW,
      canvasHeight: cssH,
    });
  }, [viewport, currentPage?.imageWidth, currentPage?.imageHeight]);

  // ── R-157 — Image layer renders ONLY when viewport / page metadata
  // changes. drawingRect / zoneMappings / suggestions are intentionally
  // absent — those only affect the overlay layer.
  useEffect(() => {
    paintImageLayer();
  }, [paintImageLayer]);

  // ── R-157 — Overlay layer rAF loop. Re-runs only when overlay inputs
  // change. The canvas is cleared per frame, but it carries no bitmap, so
  // there is no perceptible repaint cost and no flicker on the underlying
  // image (which lives on the separate back canvas).
  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio ?? 1;

    function frame() {
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cssW = canvas.width / dpr;
      const cssH = canvas.height / dpr;
      const pageW = currentPage?.imageWidth ?? cssW;
      const pageH = currentPage?.imageHeight ?? cssH;

      renderZoneOverlayLayer({
        ctx,
        viewport,
        pageWidth: pageW,
        pageHeight: pageH,
        canvasWidth: cssW,
        canvasHeight: cssH,
        zones: zoneMappings
          .filter((zm) => zm.zone !== null && zm.zone.pageNumber === currentPageNumber)
          .map((zm) => ({
            fieldId: zm.fieldDefinitionId,
            // zone is non-null because we filtered above
            zone: zm.zone!,
            isLinked: true,
            // R-125 (5.7) — class derived from the field's ocrExtracted flag.
            zoneClass: zm.ocrExtracted ? "ocr" : "guide",
          })),
        selectedZoneFieldId,
        ocrSuggestions: ocrSuggestions.map((s) => ({
          id: s.id,
          boundingBox: s.boundingBox,
          status: s.status,
        })),
        drawingRect,
        showDismissed,
      });

      animFrameRef.current = requestAnimationFrame(frame);
    }

    animFrameRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [
    viewport,
    zoneMappings,
    selectedZoneFieldId,
    ocrSuggestions,
    drawingRect,
    showDismissed,
    currentPage,
    currentPageNumber,
  ]);

  // ── Effect 3: keyboard shortcuts ────────────────────────────────────────
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA") {
        return;
      }

      switch (e.key) {
        case "d":
        case "D":
          setToolMode("draw");
          break;

        case "s":
        case "S":
          setToolMode("select");
          break;

        case " ":
          e.preventDefault();
          if (toolMode !== "pan") {
            prePanModeRef.current = toolMode === "draw" ? "draw" : "select";
            setToolMode("pan");
          }
          break;

        case "Escape":
          if (drawingRect) {
            cancelDraw();
          } else {
            selectZone(null);
          }
          break;

        case "Delete":
        case "Backspace":
          if (selectedZoneFieldId) {
            removeZone(selectedZoneFieldId);
          }
          break;
      }
    }

    function onKeyUp(e: KeyboardEvent) {
      if (e.key === " ") {
        setToolMode(prePanModeRef.current);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [
    toolMode,
    drawingRect,
    selectedZoneFieldId,
    setToolMode,
    cancelDraw,
    selectZone,
    removeZone,
  ]);

  // ── Helper: canvas event → normalized page coords ────────────────────────
  const getPageCoords = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): [number, number] => {
      const canvas = overlayCanvasRef.current;
      if (!canvas) return [0, 0];
      const rect = canvas.getBoundingClientRect();
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      const pageW = currentPage?.imageWidth ?? rect.width;
      const pageH = currentPage?.imageHeight ?? rect.height;
      return canvasToPage(canvasX, canvasY, viewport, pageW, pageH);
    },
    [currentPage, viewport],
  );

  // ── Mouse handlers ───────────────────────────────────────────────────────
  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      // R-125 (5.3) — middle-button (button 1) is always pan; right-button
      // (button 2) we let the browser handle. Left-button (0) drives the
      // mode-specific logic below.
      if (e.button === 1) {
        e.preventDefault();
        isPanDragRef.current = true;
        isDraggingRef.current = true;
        dragStartRef.current = { x: e.clientX - viewport.x, y: e.clientY - viewport.y };
        return;
      }
      if (e.button !== 0) return;

      const [px, py] = getPageCoords(e);

      if (toolMode === "draw") {
        startDraw(px, py);
        isDraggingRef.current = true;
        return;
      }

      if (toolMode === "pan") {
        isPanDragRef.current = true;
        isDraggingRef.current = true;
        dragStartRef.current = { x: e.clientX - viewport.x, y: e.clientY - viewport.y };
        return;
      }

      // select mode — check resize handles first, then zone hit, then PAN
      // (R-125 5.3 — empty-area click-drag pans rather than just deselecting).
      if (toolMode === "select") {
        const HANDLE_RADIUS_NORMALIZED = 8 / (viewport.scale * (currentPage?.imageWidth ?? 800));

        if (selectedZoneFieldId) {
          const selectedMapping = zoneMappings.find(
            (zm) => zm.fieldDefinitionId === selectedZoneFieldId,
          );
          if (selectedMapping?.zone) {
            const handle = hitTestHandle(px, py, selectedMapping.zone, HANDLE_RADIUS_NORMALIZED);
            if (handle) {
              resizeHandleRef.current = handle;
              resizingFieldIdRef.current = selectedZoneFieldId;
              isDraggingRef.current = true;
              return;
            }
          }
        }

        // Check zone hit
        for (const zm of zoneMappings) {
          if (!zm.zone || zm.zone.pageNumber !== currentPageNumber) continue;
          if (hitTestZone(px, py, zm.zone)) {
            selectZone(zm.fieldDefinitionId);
            return;
          }
        }

        // R-125 (5.3) — empty area in select mode → start a pan drag. The
        // existing pan-tool-mode UX is preserved; this just removes the
        // need to flip tool modes to reach off-screen page area.
        isPanDragRef.current = true;
        isDraggingRef.current = true;
        dragStartRef.current = { x: e.clientX - viewport.x, y: e.clientY - viewport.y };
      }
    },
    [
      toolMode,
      viewport,
      currentPage,
      selectedZoneFieldId,
      zoneMappings,
      currentPageNumber,
      getPageCoords,
      startDraw,
      selectZone,
    ],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDraggingRef.current) return;

      // R-125 (5.3) — pan drag covers all three triggers (pan tool, middle
      // button, empty-area drag in select mode).
      if (isPanDragRef.current) {
        setViewport({
          x: e.clientX - dragStartRef.current.x,
          y: e.clientY - dragStartRef.current.y,
        });
        return;
      }

      if (toolMode === "draw" && drawingRect) {
        const [px, py] = getPageCoords(e);
        updateDraw(px, py);
        return;
      }

      if (toolMode === "select" && resizeHandleRef.current && resizingFieldIdRef.current) {
        const [px, py] = getPageCoords(e);
        const mapping = zoneMappings.find(
          (zm) => zm.fieldDefinitionId === resizingFieldIdRef.current,
        );
        if (mapping?.zone) {
          const newZoneRect = resizeZone(mapping.zone, resizeHandleRef.current, px, py);
          updateZone(resizingFieldIdRef.current, {
            ...newZoneRect,
            pageNumber: currentPageNumber,
          });
        }
      }
    },
    [
      toolMode,
      drawingRect,
      zoneMappings,
      currentPageNumber,
      getPageCoords,
      updateDraw,
      setViewport,
      updateZone,
    ],
  );

  // Pull `finishDraw` from the store for the auto-commit path below. Reading
  // it via getState keeps the callback stable across renders.
  const finishDraw = useZoneEditorStore((s) => s.finishDraw);

  const onMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      // Middle-button release ends a pan drag.
      const isMiddleRelease = e.button === 1 && isPanDragRef.current;
      if (e.button !== 0 && !isMiddleRelease) return;

      // R-125 (5.3) — close any active pan drag without touching draw state.
      if (isPanDragRef.current) {
        isPanDragRef.current = false;
        isDraggingRef.current = false;
        return;
      }

      if (toolMode === "draw" && isDraggingRef.current) {
        // R-125 (5.6) — If the operator entered draw mode via
        // selectFieldForDraw (the standard new flow), `selectedZoneFieldId`
        // names the target field; commit the drawn zone directly. Otherwise,
        // hand off to the legacy parent callback path.
        const targetFieldId = selectedZoneFieldId;
        if (targetFieldId && drawingRect) {
          finishDraw(targetFieldId);
        } else if (onZoneDrawn && drawingRect) {
          const { startX, startY, currentX, currentY } = drawingRect;
          const x = Math.min(startX, currentX);
          const y = Math.min(startY, currentY);
          const width = Math.abs(currentX - startX);
          const height = Math.abs(currentY - startY);
          if (width >= 0.005 && height >= 0.005) {
            onZoneDrawn(x, y, width, height);
          }
          cancelDraw();
        } else {
          cancelDraw();
        }
      }

      isDraggingRef.current = false;
      resizeHandleRef.current = null;
      resizingFieldIdRef.current = null;
    },
    [toolMode, drawingRect, onZoneDrawn, cancelDraw, finishDraw, selectedZoneFieldId],
  );

  // ── Wheel zoom (R-95 + R-125 5.2 — Ctrl/⌘+wheel) ────────────────────────
  // React binds `wheel` as a passive root listener, so a synthetic onWheel
  // handler's preventDefault() is silently ignored (console spam; zoom/pan and
  // fit-to-canvas-on-load break). Attach a *native* non-passive listener on the
  // overlay canvas element instead so preventDefault() is honored.
  //
  // R-125 (5.2) — Ctrl/⌘ + wheel zooms; plain wheel without a modifier scrolls
  // (lets the browser pan the canvas wrapper). Plain-wheel zoom is also
  // supported for back-compat when no other modifier is held.
  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
      const newScale = Math.min(4.0, Math.max(0.1, viewport.scale * zoomFactor));
      const scaleRatio = newScale / viewport.scale;
      const newX = mouseX - scaleRatio * (mouseX - viewport.x);
      const newY = mouseY - scaleRatio * (mouseY - viewport.y);
      setViewport({ scale: newScale, x: newX, y: newY });
    };
    canvas.addEventListener("wheel", handler, { passive: false });
    return () => canvas.removeEventListener("wheel", handler);
  }, [viewport, setViewport]);

  // R-125 (5.2) — expose live canvas/page sizes to the parent so the
  // viewport-controls toolbar (rendered outside this component) can run
  // fit-to-page / fit-to-width math against the current DOM.
  useImperativeHandle(
    ref,
    () => ({
      getCanvasSize: () => {
        const canvas = overlayCanvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        return { width: rect.width, height: rect.height };
      },
      getPageSize: () => {
        if (!currentPage) return null;
        return {
          width: currentPage.imageWidth,
          height: currentPage.imageHeight,
        };
      },
    }),
    [currentPage],
  );

  // ── Cursor style ─────────────────────────────────────────────────────────
  // R-125 (5.3) — `grab` cue in pan tool mode and select mode (where an
  // empty-area drag also pans). Draw mode keeps the crosshair.
  const cursor =
    toolMode === "draw"
      ? "crosshair"
      : toolMode === "pan"
        ? "grab"
        : "default";

  // R-157 — Two stacked canvases. The wrapper is positioned `relative` so the
  // child canvases stack on top of each other. The image (back) canvas is
  // `pointer-events: none` so all mouse input goes to the overlay (front)
  // canvas. The image only re-renders on viewport / page changes; the overlay
  // re-renders per frame during interaction but carries no bitmap, so the
  // page image never flickers off during a draw.
  return (
    <div
      ref={wrapperRef}
      data-testid="zone-editor-canvas-wrapper"
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <canvas
        ref={imageCanvasRef}
        data-testid="zone-editor-image-canvas"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
          pointerEvents: "none",
        }}
      />
      <canvas
        ref={overlayCanvasRef}
        data-testid="zone-editor-overlay-canvas"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
          cursor,
          pointerEvents: "auto",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
  },
);
